import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Paper, TableContainer, Pagination, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, Grid, Card, Tooltip, IconButton, InputAdornment, Chip, Checkbox, ListItemText } from '@mui/material';
import { Link, Link as RouterLink } from 'react-router-dom';
import { getUsersList } from 'User/Domain/Services/GetUsersListService';
import { deleteUser } from 'User/Domain/Services/DeleteUserService';
import { patchUser } from 'User/Domain/Services/PatchUserService';
import { Delete, Edit, Person, Restore, Search, SwitchAccount } from '@mui/icons-material';
import { toast } from "react-toastify";
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';
import Loader from 'UI/Application/Components/Loaders/Loader';
import DialogLoader from 'UI/Application/Components/Loaders/DialogLoader';
import { PRIVATE_ADMIN_FORM_USER, PRIVATE_USER_FORM_USER } from '../Constants/PathsConstant';
import { useTranslation } from 'react-i18next';
import { WRITE_USERS, MANAGE_USER_PHOTOS } from 'Shared/Security/Application/Constants/PermissionsConstant';
import { getCompanyList } from 'Company/Domain/Services/GetCompanyListService';

function TableUserView() {

  const { t } = useTranslation();

  const [sendingApiRequest, setSendingApiRequest] = React.useState(false);
  const { isAdmin, checkPermission, impersonate, getMeFromLocalStorage } = useAuthContext();
  const canWriteUsers = isAdmin() || checkPermission(WRITE_USERS);
  const canManagePhotos = checkPermission(MANAGE_USER_PHOTOS);
  var readonly = !canWriteUsers && !canManagePhotos;
  const limit = 10;
  const [offset, setOffset] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [lastPage, setLastPage] = React.useState(1);
  const [users, setUsers] = React.useState([]);
  const [usersRows, setUsersRows] = React.useState([]);
  const [loadUsers, setLoadUsers] = React.useState(false);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [openRestoreModal, setOpenRestoreModal] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState(null);
  const [openToggleModal, setOpenToggleModal] = React.useState(false);
  const [toggleUserEnabled, setToggleUserEnabled] = React.useState(false);
  const [openImpersonateModal, setOpenImpersonateModal] = React.useState(false);
  const [impersonateTargetUser, setImpersonateTargetUser] = React.useState(null);
  const [filterUser, setFilterUser] = React.useState('');
  const [filterEnabled, setFilterEnabled] = React.useState(1);
  const [filterDeleted, setFilterDeleted] = React.useState(0);
  const [filterTerminationDate, setFilterTerminationDate] = React.useState('Todos');
  const [companies, setCompanies] = React.useState([]);
  const [filterCompanies, setFilterCompanies] = React.useState([]);

  // Fetch user data on mount or when loadUsers flag changes
  React.useEffect(() => {
    if (loadUsers) {
      var data = {
        offset: offset,
        limit: limit
      };
      data = {
        ...data,
        filterUser: filterUser,
        filterEnabled: filterEnabled === 'Todos' ? null : filterEnabled,
        filterTerminationDate: filterTerminationDate === 'Todos' ? null : filterTerminationDate,
        filterDeleted: filterDeleted,
        filterCompanies: filterCompanies.length > 0 ? filterCompanies : null
      };
      getUsersList(data).then((response) => {
        setUsers(response.records);
        setLastPage(Math.ceil(response.total / limit));
        setLoadUsers(false);
        if (Math.ceil(response.total / limit) < currentPage) {
          setCurrentPage(Math.ceil(response.total / limit));
          if (((Math.ceil(response.total / limit) - 1) * limit) < 0) {
            setOffset(0);
          } else {
            setOffset((Math.ceil(response.total / limit) - 1) * limit);
            setLoadUsers(true);
          }
        }
      });
    }
  }, [
    loadUsers,
    offset,
    limit,
    filterUser,
    filterEnabled,
    filterTerminationDate,
    filterCompanies
  ]);

  React.useEffect(() => {
    getCompanyList().then((response) => {
      if (response) {
        setCompanies(response.records);
        const defaultCompanies = response.records
          .filter(c => c.secondaryId === "4" || c.secondaryId === "13")
          .map(c => c.id);
        if (defaultCompanies.length > 0) {
          setFilterCompanies(defaultCompanies);
        }
      }
      setLoadUsers(true);
    });
  }, []);

  const handleFilterUser = (event) => {
    setFilterUser(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadUsers(true);
  };

  const handleFilterEnabledChange = (event) => {
    setFilterEnabled(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadUsers(true);
  };

  const handleFilterDeletedChange = (event) => {
    setFilterDeleted(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadUsers(true);
  };

  const handleFilterCompaniesChange = (event) => {
    const {
      target: { value },
    } = event;
    setFilterCompanies(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
    setOffset(0);
    setCurrentPage(1);
    setLoadUsers(true);
  };

  const handleFilterTerminationDateChange = (event) => {
    setFilterTerminationDate(event.target.value);
    setOffset(0);
    setCurrentPage(1);
    setLoadUsers(true);
  };

  const handleDeleteUser = (userId) => {
    setSelectedId(userId);
    setOpenDeleteModal(true);
  };

  const handleRestoreUser = (userId) => {
    setSelectedId(userId);
    setOpenRestoreModal(true);
  };

  const handleToggleUser = (userId, enabled) => {
    if (!readonly) {
      setSelectedId(userId);
      setToggleUserEnabled(enabled);
      setOpenToggleModal(true);
    }
  };

  const handleImpersonateUser = (user) => {
    setImpersonateTargetUser(user);
    setOpenImpersonateModal(true);
  };

  const handleConfirmImpersonate = async () => {
    if (!impersonateTargetUser) return;
    setSendingApiRequest(true);
    try {
      await impersonate(impersonateTargetUser.id);
    } catch (error) {
      toast.error('Error al entrar como usuario');
      setSendingApiRequest(false);
      setOpenImpersonateModal(false);
    }
  };

  const handleConfirmToggle = () => {
    if (selectedId && !sendingApiRequest) {
      setSendingApiRequest(true);
      patchUser(selectedId, {
        enabled: !toggleUserEnabled
      }).then((response) => {
        if (response) {
          setOpenToggleModal(false);
          setLoadUsers(true);
          setSelectedId(null);
          toast.success(t('user.table.modal.enable.done'));
        }
      }).finally(() => {
        setSendingApiRequest(false);
      });
    }
  };

  const handleConfirmDelete = () => {
    if (selectedId && !sendingApiRequest) {
      setSendingApiRequest(true);
      deleteUser(selectedId).then((response) => {
        if (response) {
          setOpenDeleteModal(false);
          setLoadUsers(true);
          setSelectedId(null);
          toast.success(t('user.table.modal.delete.done'));
        }
      }).finally(() => {
        setSendingApiRequest(false);
      });
    }
  };

  const handleConfirmRestore = () => {
    if (selectedId && !sendingApiRequest) {
      setSendingApiRequest(true);
      patchUser(selectedId, {
        restoreUser: true
      }).then((response) => {
        if (response) {
          setOpenRestoreModal(false);
          setLoadUsers(true);
          setSelectedId(null);
          toast.success(t('user.table.modal.restore.done'));
        }
      }).finally(() => {
        setSendingApiRequest(false);
      });
    }
  };

  // Update table rows based on users
  React.useEffect(() => {
    const rows = users.map((user) => (
      <TableRow 
        key={user.id}
        sx={{ '&:hover': { bgcolor: 'action.hover' } }}
      >
        <TableCell align='center'>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {user.name} {user.firstSurname} {user.secondSurname}
          </Typography>
        </TableCell>
        <TableCell align='center'>{user.jobPosition?.name || '-'}</TableCell>
        <TableCell align='center'>{user.email}</TableCell>
        <TableCell align='center'>{user.dni}</TableCell>
        <TableCell align='center' style={isAdmin() ? {  } : { display: 'none'}}>
          <Chip 
            label={user.roles.includes('ROLE_ADMIN') ? 'Administrador' : 'Usuario'} 
            variant="outlined" 
            size="small" 
            color={user.roles.includes('ROLE_ADMIN') ? 'primary' : 'default'}
          />
        </TableCell>
        <TableCell align="center">
          <Tooltip title={user.enabled ? t('user.table.modal.enable.disable') : t('user.table.modal.enable.enable')}>
            <IconButton 
              color={user.enabled ? 'success' : 'error'} 
              onClick={() => handleToggleUser(user.id, user.enabled)}
              size="small"
            >
              <Person />
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell align="center" style={!readonly ? {  } : { display: 'none'}}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          {user.deletedAt === null ? (<>
            {isAdmin() && user.id !== getMeFromLocalStorage()?.id && (
              <Tooltip title={t('user.table.impersonate') || 'Entrar como'}>
                <IconButton 
                  color="warning" 
                  onClick={() => handleImpersonateUser(user)} 
                  size="small"
                >
                  <SwitchAccount />
                </IconButton>
              </Tooltip>
            )}
            {canWriteUsers && (
              <Tooltip title={t('user.table.modal.delete.button.delete')}>
                <IconButton color="error" onClick={() => handleDeleteUser(user.id)} size="small">
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('user.table.button.edit') || 'Editar'}>
              <IconButton 
                color="primary" 
                component={Link} 
                to={isAdmin() ? (`${PRIVATE_ADMIN_FORM_USER}/${user.id}`) : (`${PRIVATE_USER_FORM_USER}/${user.id}`)}
                size="small"
              >
                <Edit />
              </IconButton>
            </Tooltip>
            </>) : (
            <Tooltip title={t('user.table.modal.restore.button.restore')}>
              <IconButton color="success" onClick={() => handleRestoreUser(user.id)} size="small">
                <Restore />
              </IconButton>
            </Tooltip>
          )}
          </Box>
        </TableCell>
      </TableRow>
    ));
    setUsersRows(rows);
  }, [users]);

  // Update offset when page changes
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
    if (((newPage - 1) * limit) < 0) {
      setOffset(0);
    } else {
      setOffset((newPage - 1) * limit);
    }
    setLoadUsers(true);
  };
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {t('user.table.title')}
        </Typography>
        {!readonly && (
          <Button
            variant="contained"
            startIcon={<Person />}
            component={RouterLink}
            to={isAdmin() ? (PRIVATE_ADMIN_FORM_USER + '/new') : (PRIVATE_USER_FORM_USER + '/new')}
            sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
          >
            {t('user.table.button.create')}
          </Button>
        )}
      </Box>

      <Card sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 'bold', ml: 0.5 }}>
                {t('user.table.filter_by_User')}
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Buscar usuario..."
                value={filterUser}
                onChange={handleFilterUser}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 'bold', ml: 0.5 }}>
                {t('user.table.filter_by_status')}
              </Typography>
              <Select
                size="small"
                value={filterEnabled}
                onChange={handleFilterEnabledChange}
              >
                <MenuItem value={'Todos'}>{t('user.table.filter_by_status_all')}</MenuItem>
                <MenuItem value={1}>{t('user.table.filter_by_status_enabled')}</MenuItem>
                <MenuItem value={0}>{t('user.table.filter_by_status_disabled')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 'bold', ml: 0.5 }}>
                {t('user.table.filter_by_deleted')}
              </Typography>
              <Select
                size="small"
                value={filterDeleted}
                onChange={handleFilterDeletedChange}
              >
                <MenuItem value={0}>{t('user.table.filter_by_deleted_not_deleted')}</MenuItem>
                <MenuItem value={1}>{t('user.table.filter_by_deleted_deleted')}</MenuItem>
                <MenuItem value={2}>{t('user.table.filter_by_deleted_all')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 'bold', ml: 0.5 }}>
                Fecha Terminación
              </Typography>
              <Select
                size="small"
                value={filterTerminationDate}
                onChange={handleFilterTerminationDateChange}
              >
                <MenuItem value={'Todos'}>Todas</MenuItem>
                <MenuItem value={1}>Con fecha</MenuItem>
                <MenuItem value={0}>Sin fecha</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 'bold', ml: 0.5 }}>
                Empresa
              </Typography>
              <Select
                multiple
                size="small"
                value={filterCompanies}
                onChange={handleFilterCompaniesChange}
                renderValue={(selected) => {
                  if (selected.length === 0) return "Todas";
                  return companies.filter(c => selected.includes(c.id)).map(c => c.name).join(', ');
                }}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    <Checkbox checked={filterCompanies.indexOf(company.id) > -1} size="small" />
                    <ListItemText primary={company.name} primaryTypographyProps={{ variant: 'body2' }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

        </Grid>
      </Card>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell align='center' sx={{ fontWeight: 'bold' }}>{t('user.table.name')}</TableCell>
              <TableCell align='center' sx={{ fontWeight: 'bold' }}>{t('user.table.cargo')}</TableCell>
              <TableCell align='center' sx={{ fontWeight: 'bold' }}>{t('user.table.email')}</TableCell>
              <TableCell align='center' sx={{ fontWeight: 'bold' }}>{t('user.table.dni')}</TableCell>
              <TableCell align='center' sx={{ fontWeight: 'bold', display: isAdmin() ? 'table-cell' : 'none' }}>{t('user.table.rol')}</TableCell>
              <TableCell align='center' sx={{ fontWeight: 'bold' }}>{t('user.table.status')}</TableCell>
              <TableCell align='center' sx={{ fontWeight: 'bold', display: !readonly ? 'table-cell' : 'none' }}>{t('user.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadUsers ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Loader />
                </TableCell>
              </TableRow>
            ) : usersRows.length > 0 ? (
              usersRows
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                  <Typography variant="body1" color="text.secondary">
                    No se encontraron usuarios que coincidan con los filtros.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Pagination count={lastPage} page={currentPage} onChange={handlePageChange} />
      </Box>

      {/* Modals */}
      <Dialog 
        open={openDeleteModal} 
        onClose={() => setOpenDeleteModal(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>{t('user.table.modal.delete.title')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body1">{t('user.table.modal.delete.phrase')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setOpenDeleteModal(false)}
            sx={{ textTransform: 'uppercase', fontWeight: 'bold', color: 'info.main', mr: 1 }}
          >
            {t('user.table.modal.delete.button.cancel')}
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleConfirmDelete}
            sx={{ textTransform: 'uppercase', fontWeight: 'bold', borderRadius: 2, px: 3, boxShadow: 2 }}
          >
            {t('user.table.modal.delete.button.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={openRestoreModal} 
        onClose={() => setOpenRestoreModal(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>{t('user.table.modal.restore.title')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body1">{t('user.table.modal.restore.phrase')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setOpenRestoreModal(false)}
            sx={{ textTransform: 'uppercase', fontWeight: 'bold', color: 'info.main', mr: 1 }}
          >
            {t('user.table.modal.restore.button.cancel')}
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleConfirmRestore}
            sx={{ textTransform: 'uppercase', fontWeight: 'bold', borderRadius: 2, px: 3, boxShadow: 2 }}
          >
            {t('user.table.modal.restore.button.restore')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={openToggleModal} 
        onClose={() => setOpenToggleModal(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          {toggleUserEnabled ? t('user.table.modal.enable.disable') : t('user.table.modal.enable.enable')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body1">
            {toggleUserEnabled ? t('user.table.modal.enable.phrase.disable') : t('user.table.modal.enable.phrase.enable')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setOpenToggleModal(false)}
            sx={{ textTransform: 'uppercase', fontWeight: 'bold', color: 'info.main', mr: 1 }}
          >
            {t('user.table.modal.enable.button.cancel')}
          </Button>
          <Button 
            variant="contained" 
            color={toggleUserEnabled ? 'error' : 'success'} 
            onClick={handleConfirmToggle}
            sx={{ textTransform: 'uppercase', fontWeight: 'bold', borderRadius: 2, px: 3, boxShadow: 2 }}
          >
            {toggleUserEnabled ? t('user.table.modal.enable.disable') : t('user.table.modal.enable.enable')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={openImpersonateModal} 
        onClose={() => setOpenImpersonateModal(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 1, minWidth: 320 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          {t('user.table.modal.impersonate.title') || 'Entrar como usuario'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body1">
            {t('user.table.modal.impersonate.phrase') || '¿Estás seguro de que quieres iniciar sesión como este usuario?'}
          </Typography>
          {impersonateTargetUser && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {impersonateTargetUser.name} {impersonateTargetUser.firstSurname} {impersonateTargetUser.secondSurname}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {impersonateTargetUser.email} &bull; {impersonateTargetUser.jobPosition?.name || 'Sin puesto asignado'}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Podrás volver a tu usuario de administrador en cualquier momento con el botón "Volver usuario original".
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setOpenImpersonateModal(false)}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {t('user.table.modal.impersonate.button.cancel') || 'Cancelar'}
          </Button>
          <Button 
            variant="contained" 
            color="warning" 
            onClick={handleConfirmImpersonate}
            startIcon={<SwitchAccount />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', px: 2 }}
          >
            {t('user.table.modal.impersonate.button.confirm') || 'Entrar como'}
          </Button>
        </DialogActions>
      </Dialog>

      <DialogLoader open={sendingApiRequest} />
    </Box>
  );
}

export default TableUserView;
